// SensorPackDetailPage.js - Only keeping the 30-minute default time range
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  TextField,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import MetricLineChart from '../components/MetricLineChart';
import { useAuth } from '../contexts/AuthContext';
import { getSensorPackById, getMetricStandards, getFilteredMetrics, exportMetricsAsCSV } from '../services/sensorPackService';
import config from '../config';

const SensorPackDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [sensorPack, setSensorPack] = useState(null);
  const [metricStandards, setMetricStandards] = useState([]);
  const [loadingSensorPack, setLoadingSensorPack] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // State for chart data
  const [chartData, setChartData] = useState({
    raw: [],
    calibrated: []
  });

  // Define sensor types
  const raw_sensor_types = ['gas_co2_k30_ppm', 'water_turbidity', 'water_cdom', 'water_chla', 'water_conductivity_siemens_per_cm', 'temp_raw', 'airmar_air_temperature',
    'airmar_wind_speed', 'airmar_barometric_pressure',  'airmar_wind_direction_true', 'airmar_pitch', 'airmar_roll', 'gas_baro_pressure', 'gas_baro_humidity'];
  const calibrated_sensor_types = ['temperature', 'conductivity', 'salinity', 'ph', 'o2', 'co2_k30'];

  // State for selected sensors
  const [selectedRawSensors, setSelectedRawSensors] = useState({});
  const [selectedCalibratedSensors, setSelectedCalibratedSensors] = useState({});

  // Date range state
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');

  // Format a Date object to local datetime string (YYYY-MM-DDThh:mm)
  const formatToLocalDateTimeString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Convert local datetime to UTC
  const convertToUTC = (localDateTimeString) => {
    if (!localDateTimeString) return '';
    const date = new Date(localDateTimeString);
    return date.toISOString();
  };

  // Initialize checkbox states
  useEffect(() => {
    const initRawSensors = {};
    raw_sensor_types.forEach(sensor => {
      initRawSensors[sensor] = false;
    });
    setSelectedRawSensors(initRawSensors);

    const initCalibratedSensors = {};
    calibrated_sensor_types.forEach(sensor => {
      initCalibratedSensors[sensor] = false;
    });
    setSelectedCalibratedSensors(initCalibratedSensors);
  }, []);

  useEffect(() => {
    const fetchSensorPack = async () => {
      try {
        const data = await getSensorPackById(id, token);
        setSensorPack(data);
        setLoadingSensorPack(false);

        // Set default dates (current time to 30 minutes ago in user's local timezone)
        const endDate = new Date(); // Current time in local timezone
        const startDate = new Date();
        startDate.setMinutes(startDate.getMinutes() - 30); // 30 minutes ago

        setEndDateTime(formatToLocalDateTimeString(endDate));
        setStartDateTime(formatToLocalDateTimeString(startDate));

      } catch (err) {
        console.error(`Error fetching sensor pack ${id}:`, err);
        setError(`Failed to fetch sensor pack details`);
        setLoadingSensorPack(false);
      }
    };

    if (token) {
      fetchSensorPack();
    }
  }, [id, token]);

  // Handle checkbox changes for raw sensors
  const handleRawSensorChange = (event) => {
    setSelectedRawSensors({
      ...selectedRawSensors,
      [event.target.name]: event.target.checked
    });
  };

  // Handle checkbox changes for calibrated sensors
  const handleCalibratedSensorChange = (event) => {
    setSelectedCalibratedSensors({
      ...selectedCalibratedSensors,
      [event.target.name]: event.target.checked
    });
  };

  // Handle chart update using getFilteredMetrics service
  const handleUpdateCharts = async () => {
    if (!sensorPack) return;

    try {
      setLoadingMetrics(true);
      setError('');

      // Get selected raw sensors
      const selectedRaw = Object.keys(selectedRawSensors).filter(key => selectedRawSensors[key]);
      // Get selected calibrated sensors
      const selectedCalibrated = Object.keys(selectedCalibratedSensors).filter(key => selectedCalibratedSensors[key]);

      // If no sensors selected, show error or info message
      if (selectedRaw.length === 0 && selectedCalibrated.length === 0) {
        setError('Please select at least one sensor to display');
        setLoadingMetrics(false);
        return;
      }

      // Convert local datetime to UTC
      const utcStartDate = convertToUTC(startDateTime);
      const utcEndDate = convertToUTC(endDateTime);

      // Call getFilteredMetrics service
      const data = await getFilteredMetrics(token, {
        sensorPackId: sensorPack.blues_device_id,
        rawSensors: selectedRaw,
        calibratedSensors: selectedCalibrated,
        startDate: utcStartDate,
        endDate: utcEndDate
      });

      // Update chart data
      setChartData({
        raw: data.standard || [], // Change from 'raw' to 'standard'
        calibrated: data.calibrated || []
      });

      setLoadingMetrics(false);

    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Failed to fetch sensor data');
      setLoadingMetrics(false);
    }
  };

  // Handle CSV download
  const handleCSVDownload = async () => {
    if (!sensorPack) return;

    try {
      setLoadingCSV(true);
      setError('');

      // Get selected raw sensors
      const selectedRaw = Object.keys(selectedRawSensors).filter(key => selectedRawSensors[key]);
      // Get selected calibrated sensors
      const selectedCalibrated = Object.keys(selectedCalibratedSensors).filter(key => selectedCalibratedSensors[key]);

      // If no sensors selected, show error or info message
      if (selectedRaw.length === 0 && selectedCalibrated.length === 0) {
        setError('Please select at least one sensor to download');
        setLoadingCSV(false);
        return;
      }

      // Convert local datetime to UTC
      const utcStartDate = convertToUTC(startDateTime);
      const utcEndDate = convertToUTC(endDateTime);

      // Call exportMetricsAsCSV service
      const blob = await exportMetricsAsCSV(token, {
        sensorPackId: sensorPack.blues_device_id,
        rawSensors: selectedRaw,
        calibratedSensors: selectedCalibrated,
        startDate: utcStartDate,
        endDate: utcEndDate
      });

      // Create a download link for a zip file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sensor-data-${sensorPack.blues_device_id}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError('Failed to download sensor data');
    } finally {
      setLoadingCSV(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loadingSensorPack) {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleBack}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </>
    );
  }

  // Helper function to get sensor display name
  const getSensorDisplayName = (key) => {
    const nameMap = {
      'gas_co2_k30_ppm': 'raw CO2 (ppm)',
      'water_turbidity': 'Turbidity',
      'water_cdom': 'CDOM',
      'water_chla': 'Chlorophyll',
      'temp_raw': 'Raw Temperature',
      'airmar_air_temperature': 'Air Temperature',
      'airmar_wind_speed': 'Wind Speed',
      'airmar_wind_direction_true': "Wind Direction",
      'airmar_barometric_pressure': 'Airmar Baro Pressure',
      'gas_baro_pressure': 'Gas Barometric Pressure',
      'gas_baro_humidity': 'Gas Barometric Humidity',
      'airmar_pitch': 'Pitch',
      'airmar_roll': 'Roll',
      'salinity': 'Salinity',
      'temperature': 'Temperature',
      'conductivity': 'Conductivity',
      'ph': 'pH',
      'o2': 'Oxygen',
      'co2_k30': 'CO2'
    };

    return nameMap[key] || key;
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={handleBack}
        >
          Back
        </Button>

        {sensorPack && (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {sensorPack.description}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sensor Pack Details
                    </Typography>
                    <Typography variant="body1">
                      <strong>ID:</strong> {sensorPack.id}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Device ID:</strong> {sensorPack.blues_device_id}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Sensor Metrics Visualization
          </Typography>

          {/* Date Range Selection */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography component="div" variant="h6" gutterBottom>
                  Time Range
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5} md={3}>
                    <TextField
                      label="Start Date & Time"
                      type="datetime-local"
                      fullWidth
                      value={startDateTime}
                      onChange={(e) => setStartDateTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: { xs: 2, sm: 0 } }}
                      InputProps={{
                        inputProps: { max: new Date().toISOString().slice(0, 16) }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} md={3}>
                    <TextField
                      label="End Date & Time"
                      type="datetime-local"
                      fullWidth
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: { xs: 2, sm: 0 } }}
                      InputProps={{
                        inputProps: { max: new Date().toISOString().slice(0, 16) }
                      }}
                    />
                  </Grid>
                  <Grid item xs={5} sm={2} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '30px', justifyContent: 'flex-start' }}>
                      <Button
                        sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                        variant="outlined"
                        onClick={handleCSVDownload}
                        disabled={loadingCSV}
                        startIcon={loadingCSV ? <CircularProgress size={20} /> : null}
                      >
                        {loadingCSV ? 'Downloading...' : 'CSV Download'}
                      </Button>
                    </Box>
                    <Box>
                    <Button
                        sx={{whiteSpace: 'nowrap', marginTop: '15px', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: '150px'}}
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateCharts}
                      >
                        Update Charts
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {/* Sensor Selection */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              {/* Raw Sensors */}
              <Grid item xs={12} md={6}>
                <Typography component="div" variant="h6" gutterBottom>
                  Raw Sensors
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {raw_sensor_types.map((sensor) => (
                    <FormControlLabel
                      key={sensor}
                      control={
                        <Checkbox
                          name={sensor}
                          checked={selectedRawSensors[sensor] || false}
                          onChange={handleRawSensorChange}
                        />
                      }
                      label={getSensorDisplayName(sensor)}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Calibrated Sensors */}
              <Grid item xs={12} md={6}>
                <Typography component="div" variant="h6" gutterBottom>
                  Calibrated Sensors
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {calibrated_sensor_types.map((sensor) => (
                    <FormControlLabel
                      key={sensor}
                      control={
                        <Checkbox
                          name={sensor}
                          checked={selectedCalibratedSensors[sensor] || false}
                          onChange={handleCalibratedSensorChange}
                        />
                      }
                      label={getSensorDisplayName(sensor)}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
            {(chartData.raw.length > 0 || chartData.calibrated.length > 0) ? (
              `Showing sensor data from ${new Date(startDateTime).toLocaleString()} to ${new Date(endDateTime).toLocaleString()}`
            ) : (
              'Please select sensors and date range, then click "Update Charts"'
            )}
          </Typography>

          {loadingMetrics ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (chartData.raw.length === 0 && chartData.calibrated.length === 0) ? (
            <Typography variant="body1" color="textSecondary" align="center">
              No data available. Please select sensors and date range, then click "Update Charts".
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {/* Raw Sensor Charts */}
              {Object.keys(selectedRawSensors)
                .filter(sensor => selectedRawSensors[sensor])
                .map(sensor => (
                  <Grid item xs={12} key={`raw-${sensor}`}>
                    <MetricLineChart
                      title={getSensorDisplayName(sensor)}
                      data={chartData.raw}
                      metricKey={sensor}
                      color="#ff7043"
                    />
                  </Grid>
              ))}

              {/* Calibrated Sensor Charts */}
              {Object.keys(selectedCalibratedSensors)
                .filter(sensor => selectedCalibratedSensors[sensor])
                .map(sensor => (
                  <Grid item xs={12} key={`calibrated-${sensor}`}>
                    <MetricLineChart
                      title={getSensorDisplayName(sensor)}
                      data={chartData.calibrated}
                      metricKey={sensor}
                      color="#5c6bc0"
                    />
                  </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Paper>
    </>
  );
};

export default SensorPackDetailPage;